import React, { useState, useEffect } from 'react';
import { Button, TextInput, Textarea } from '@mantine/core';
import apiService from '../../services/api';
import { AdminPortalProps, ApiResponse } from '../../types';

interface AdminStatsData {
  totalClients: number;
  totalTrainers: number;
  totalSessions: number;
  totalRevenue: number;
  activeSessions: number;
}

interface AdminClient {
  id: string;
  name: string;
  email: string;
  joinDate: string;
  sessions: number;
  status: string;
  [key: string]: unknown;
}

interface AdminTrainer {
  id: string;
  name: string;
  email: string;
  joinDate: string;
  rating: number;
  sessions: number;
  status: string;
  [key: string]: unknown;
}

interface AdminSession {
  id: string;
  client: string;
  trainer: string;
  date: string;
  duration: number;
  status: string;
  revenue: number;
  [key: string]: unknown;
}

interface AdminVideo {
  id: number;
  title: string;
  description?: string;
  category: string;
  url?: string;
  isActive: boolean;
  uploadDate: string;
}

interface PendingApplication {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  business_name?: string;
  experience_years?: string | number;
  first_10_min_rate?: string | number;
  payment_completed?: boolean;
  application_submitted_at?: string;
  [key: string]: unknown;
}

interface BillingDataType {
  sessionIncome: number;
  sessionCount: number;
}

interface NewVideoData {
  title: string;
  description: string;
  category: string;
  url: string;
  isActive: boolean;
}

interface DeleteStatus {
  type: string;
  message: string;
}

interface TabDefinition {
  id: string;
  label: string;
  icon: string;
}

const AdminPortal: React.FC<AdminPortalProps> = ({ onBackToHome, onLogout }) => {
  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<AdminStatsData>({
    totalClients: 0,
    totalTrainers: 0,
    totalSessions: 0,
    totalRevenue: 0,
    activeSessions: 0
  });
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [trainers, setTrainers] = useState<AdminTrainer[]>([]);
  const [sessions, setSessions] = useState<AdminSession[]>([]);
  const [referenceVideos, setReferenceVideos] = useState<AdminVideo[]>([]);
  const [pendingApplications, setPendingApplications] = useState<PendingApplication[]>([]);
  const [processingApplication, setProcessingApplication] = useState<string | null>(null);
  const [processingTrainer, setProcessingTrainer] = useState<string | null>(null);
  const [billingData, setBillingData] = useState<BillingDataType>({ sessionIncome: 0, sessionCount: 0 });
  const [dashboardStartDate, setDashboardStartDate] = useState<string>('');
  const [dashboardEndDate, setDashboardEndDate] = useState<string>('');
  const [trainerSearchQuery, setTrainerSearchQuery] = useState<string>('');
  const [clientTotalCount, setClientTotalCount] = useState<number>(0);
  const [trainerTotalCount, setTrainerTotalCount] = useState<number>(0);
  const [newVideo, setNewVideo] = useState<NewVideoData>({
    title: '',
    description: '',
    category: '',
    url: '',
    isActive: true
  });
  const [deleteUsername, setDeleteUsername] = useState<string>('');
  const [deleteStatus, setDeleteStatus] = useState<DeleteStatus>({ type: '', message: '' });
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      // Load all admin data
      await Promise.all([
        loadStats(),
        loadClients(),
        loadTrainers(),
        loadSessions(),
        loadReferenceVideos(),
        loadPendingApplications(),
        loadBilling()
      ]);
    } catch (err) {
      console.error('Error loading admin data:', err);
      setError('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = (await apiService.getAdminStats(dashboardStartDate || null, dashboardEndDate || null)) as ApiResponse<AdminStatsData>;
      if (response.Code === 200 && response.data) {
        const data = response.data as AdminStatsData;
        setStats({
          totalClients: data.totalClients || 0,
          totalTrainers: data.totalTrainers || 0,
          totalSessions: data.totalSessions || 0,
          totalRevenue: data.totalRevenue || 0,
          activeSessions: data.activeSessions || 0
        });
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = (await apiService.getAllClients()) as ApiResponse<AdminClient[]> & { totalCount?: number };
      if (response.Code === 200 && response.data) {
        const data = response.data as AdminClient[];
        setClients(data);
        setClientTotalCount(response.totalCount || data.length);
      } else {
        console.error('Failed to load clients:', response.Message);
        setError(response.Message || 'Failed to load clients');
      }
    } catch (err) {
      console.error('Error loading clients:', err);
      setError('Failed to load clients: ' + ((err as Error).message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const loadTrainers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = (await apiService.getAllTrainersForAdmin()) as ApiResponse<AdminTrainer[]> & { totalCount?: number };
      if (response.Code === 200 && response.data) {
        const data = response.data as AdminTrainer[];
        setTrainers(data);
        setTrainerTotalCount(response.totalCount || data.length);
      } else {
        console.error('Failed to load trainers:', response.Message);
        setError(response.Message || 'Failed to load trainers');
      }
    } catch (err) {
      console.error('Error loading trainers:', err);
      setError('Failed to load trainers: ' + ((err as Error).message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async () => {
    try {
      const response = (await apiService.getAllSessions(trainerSearchQuery || null)) as ApiResponse<AdminSession[]>;
      if (response.Code === 200 && response.data) {
        setSessions(response.data as AdminSession[]);
      }
    } catch (err) {
      console.error('Error loading sessions:', err);
    }
  };

  const loadBilling = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = (await apiService.getBilling()) as ApiResponse<BillingDataType>;
      if (response.Code === 200 && response.data) {
        const data = response.data as BillingDataType;
        setBillingData({
          sessionIncome: data.sessionIncome || 0,
          sessionCount: data.sessionCount || 0
        });
      } else {
        console.error('Failed to load billing:', response.Message);
        setError(response.Message || 'Failed to load billing data');
      }
    } catch (err) {
      console.error('Error loading billing:', err);
      setError('Failed to load billing data: ' + ((err as Error).message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const loadReferenceVideos = async () => {
    // Mock data for now
    setReferenceVideos([
      { id: 1, title: 'Basic Sit Command', category: 'Obedience', isActive: true, uploadDate: '2024-01-15' },
      { id: 2, title: 'Leash Training Basics', category: 'Walking', isActive: true, uploadDate: '2024-01-20' },
      { id: 3, title: 'Puppy Socialization', category: 'Puppy', isActive: false, uploadDate: '2024-02-01' }
    ]);
  };

  const loadPendingApplications = async () => {
    try {
      const response = (await apiService.getPendingTrainerApplications()) as ApiResponse<PendingApplication[]>;
      if (response.Code === 200) {
        setPendingApplications((response.data as PendingApplication[]) || []);
      }
    } catch (err) {
      console.error('Error loading pending applications:', err);
    }
  };

  const handleAcceptApplication = async (trainerId: string) => {
    if (!window.confirm('Are you sure you want to accept this trainer application?')) {
      return;
    }

    try {
      setProcessingApplication(trainerId);
      const response = (await apiService.acceptTrainerApplication(trainerId)) as ApiResponse;
      if (response.Code === 200) {
        await loadPendingApplications();
        await loadTrainers();
        alert('Trainer application accepted successfully');
      } else {
        alert('Failed to accept application: ' + (response.Message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error accepting application:', err);
      alert('Failed to accept application: ' + ((err as Error).message || 'Unknown error'));
    } finally {
      setProcessingApplication(null);
    }
  };

  const handleDenyApplication = async (trainerId: string) => {
    const reason = window.prompt('Please provide a reason for denying this application (optional):');
    if (reason === null) {
      return; // User cancelled
    }

    if (!window.confirm('Are you sure you want to deny this trainer application? This will remove their account.')) {
      return;
    }

    try {
      setProcessingApplication(trainerId);
      const response = (await apiService.denyTrainerApplication(trainerId, reason || '')) as ApiResponse;
      if (response.Code === 200) {
        await loadPendingApplications();
        await loadTrainers();
        alert('Trainer application denied and moved to blocked trainers');
      } else {
        alert('Failed to deny application: ' + (response.Message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error denying application:', err);
      alert('Failed to deny application: ' + ((err as Error).message || 'Unknown error'));
    } finally {
      setProcessingApplication(null);
    }
  };

  const handleAddVideo = async () => {
    try {
      setLoading(true);
      // Add video logic here
      const newVideoData: AdminVideo = {
        ...newVideo,
        id: referenceVideos.length + 1,
        uploadDate: new Date().toISOString().split('T')[0]
      };
      setReferenceVideos([...referenceVideos, newVideoData]);
      setNewVideo({ title: '', description: '', category: '', url: '', isActive: true });
      setError(null);
    } catch (err) {
      setError('Failed to add video');
    } finally {
      setLoading(false);
    }
  };

  const toggleVideoStatus = (videoId: number) => {
    setReferenceVideos(videos =>
      videos.map(video =>
        video.id === videoId
          ? { ...video, isActive: !video.isActive }
          : video
      )
    );
  };

  const handleDeleteAccount = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!deleteUsername.trim()) {
      setDeleteStatus({ type: 'error', message: 'Please enter a username to delete.' });
      return;
    }

    try {
      setDeleteLoading(true);
      setDeleteStatus({ type: '', message: '' });

      const response = (await apiService.deleteUserByUsername(deleteUsername.trim())) as ApiResponse;

      if (response.Code === 200) {
        setDeleteStatus({
          type: 'success',
          message: `User '${deleteUsername.trim()}' was deleted successfully.`
        });
        setDeleteUsername('');
      } else {
        setDeleteStatus({
          type: 'error',
          message: response.Message || 'Failed to delete user.'
        });
      }
    } catch (err) {
      console.error('Failed to delete user:', err);
      setDeleteStatus({
        type: 'error',
        message: (err as Error).message || 'Failed to delete user.'
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const tabs: TabDefinition[] = [
    { id: 'dashboard', label: 'Dashboard', icon: '\uD83D\uDCCA' },
    { id: 'clients', label: 'Clients', icon: '\uD83D\uDC65' },
    { id: 'trainers', label: 'Trainers', icon: '\uD83C\uDF93' },
    { id: 'pending', label: 'Pending Applications', icon: '\u23F3' },
    { id: 'sessions', label: 'Sessions', icon: '\uD83D\uDCC5' },
    { id: 'videos', label: 'Reference Videos', icon: '\uD83C\uDFA5' },
    { id: 'billing', label: 'Billing', icon: '\uD83D\uDCB0' },
    { id: 'accounts', label: 'Account Tools', icon: '\uD83D\uDEE0\uFE0F' }
  ];

  const getStatusClasses = (status: string, activeValue: string, pendingValue?: string) => {
    if (status === activeValue) return 'bg-[rgba(46,204,113,0.2)] text-[#2ecc71]';
    if (pendingValue && status === pendingValue) return 'bg-[rgba(241,196,15,0.2)] text-[#f1c40f]';
    return 'bg-[rgba(231,76,60,0.2)] text-[#e74c3c]';
  };

  const renderDashboard = () => (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#f39c12]">Admin Dashboard</h2>
        <Button
          onClick={loadStats}
          className="bg-[#3b82f6] hover:bg-[#2563eb]"
        >
          {'\uD83D\uDD04'} Refresh
        </Button>
      </div>

      <div className="flex gap-4 mb-8 flex-wrap items-center">
        <div className="flex flex-col gap-1">
          <label className="text-[#f39c12] text-sm font-semibold">Start Date</label>
          <input
            type="date"
            value={dashboardStartDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDashboardStartDate(e.target.value)}
            className="p-2.5 rounded-lg border-2 border-white/20 bg-white/10 text-white text-base w-[200px] transition-all duration-300"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[#f39c12] text-sm font-semibold">End Date</label>
          <input
            type="date"
            value={dashboardEndDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDashboardEndDate(e.target.value)}
            className="p-2.5 rounded-lg border-2 border-white/20 bg-white/10 text-white text-base w-[200px] transition-all duration-300"
          />
        </div>
        <Button
          onClick={loadStats}
          className="bg-[#22c55e] hover:bg-[#16a34a] mt-5"
        >
          Apply Date Range
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 mb-8">
        <div className="bg-white/10 rounded-lg p-3 sm:p-5 text-center border border-white/20">
          <div className="text-2xl sm:text-3xl font-bold text-[#f39c12] mb-1">{stats.totalClients}</div>
          <div className="text-sm text-white/80">Total Clients</div>
        </div>
        <div className="bg-white/10 rounded-lg p-3 sm:p-5 text-center border border-white/20">
          <div className="text-2xl sm:text-3xl font-bold text-[#f39c12] mb-1">{stats.totalTrainers}</div>
          <div className="text-sm text-white/80">Total Trainers</div>
        </div>
        <div className="bg-white/10 rounded-lg p-3 sm:p-5 text-center border border-white/20">
          <div className="text-2xl sm:text-3xl font-bold text-[#f39c12] mb-1">{stats.totalSessions}</div>
          <div className="text-sm text-white/80">Total Sessions</div>
        </div>
        <div className="bg-white/10 rounded-lg p-3 sm:p-5 text-center border border-white/20">
          <div className="text-2xl sm:text-3xl font-bold text-[#f39c12] mb-1">${stats.totalRevenue.toLocaleString()}</div>
          <div className="text-sm text-white/80">Total Revenue</div>
        </div>
        <div className="bg-white/10 rounded-lg p-3 sm:p-5 text-center border border-white/20">
          <div className="text-2xl sm:text-3xl font-bold text-[#f39c12] mb-1">{stats.activeSessions}</div>
          <div className="text-sm text-white/80">Active Sessions</div>
        </div>
      </div>
    </div>
  );

  const renderClients = () => (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#f39c12]">Client Management</h2>
          <p className="text-white/70 mt-1">
            Total Clients (Last Month): {clientTotalCount}
          </p>
        </div>
        <Button
          onClick={loadClients}
          className="bg-[#3b82f6] hover:bg-[#2563eb]"
        >
          {'\uD83D\uDD04'} Refresh
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse bg-white/5 rounded-lg overflow-hidden">
          <thead>
            <tr>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Name</th>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Email</th>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Join Date</th>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Sessions</th>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {clients.map(client => (
              <tr key={client.id}>
                <td className="p-4 border-b border-white/10 text-white">{client.name}</td>
                <td className="p-4 border-b border-white/10 text-white">{client.email}</td>
                <td className="p-4 border-b border-white/10 text-white">{client.joinDate}</td>
                <td className="p-4 border-b border-white/10 text-white">{client.sessions}</td>
                <td className="p-4 border-b border-white/10 text-white">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusClasses(client.status, 'Active')}`}>
                    {client.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const handleAcceptTrainer = async (trainerId: string) => {
    if (!window.confirm('Are you sure you want to approve this trainer?')) {
      return;
    }

    try {
      setProcessingTrainer(trainerId);
      const response = (await apiService.acceptTrainerApplication(trainerId)) as ApiResponse;
      if (response.Code === 200) {
        await loadTrainers();
        await loadStats(); // Refresh stats to update trainer count
        alert('Trainer approved successfully');
      } else {
        alert('Failed to approve trainer: ' + (response.Message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error approving trainer:', err);
      alert('Failed to approve trainer: ' + ((err as Error).message || 'Unknown error'));
    } finally {
      setProcessingTrainer(null);
    }
  };

  const handleDenyTrainer = async (trainerId: string) => {
    const reason = window.prompt('Please provide a reason for denying this trainer (optional):');
    if (reason === null) {
      return; // User cancelled
    }

    if (!window.confirm('Are you sure you want to deny this trainer? This will remove their account.')) {
      return;
    }

    try {
      setProcessingTrainer(trainerId);
      const response = (await apiService.denyTrainerApplication(trainerId, reason || '')) as ApiResponse;
      if (response.Code === 200) {
        await loadTrainers();
        await loadStats(); // Refresh stats to update trainer count
        alert('Trainer denied and removed from system');
      } else {
        alert('Failed to deny trainer: ' + (response.Message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error denying trainer:', err);
      alert('Failed to deny trainer: ' + ((err as Error).message || 'Unknown error'));
    } finally {
      setProcessingTrainer(null);
    }
  };

  const renderTrainers = () => (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#f39c12]">Trainer Management</h2>
          <p className="text-white/70 mt-1">
            Total Trainers (Last Month): {trainerTotalCount}
          </p>
        </div>
        <Button
          onClick={loadTrainers}
          className="bg-[#3b82f6] hover:bg-[#2563eb]"
        >
          {'\uD83D\uDD04'} Refresh
        </Button>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full border-collapse bg-white/5 rounded-lg overflow-hidden">
        <thead>
          <tr>
            <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Name</th>
            <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Email</th>
            <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Join Date</th>
            <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Rating</th>
            <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Sessions</th>
            <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Status</th>
            <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {trainers.length === 0 ? (
            <tr>
              <td colSpan={7} className="p-10 border-b border-white/10 text-white text-center">
                No trainers found
              </td>
            </tr>
          ) : (
            trainers.map(trainer => (
              <tr key={trainer.id}>
                <td className="p-4 border-b border-white/10 text-white">{trainer.name}</td>
                <td className="p-4 border-b border-white/10 text-white">{trainer.email}</td>
                <td className="p-4 border-b border-white/10 text-white">{trainer.joinDate}</td>
                <td className="p-4 border-b border-white/10 text-white">{trainer.rating} {'\u2B50'}</td>
                <td className="p-4 border-b border-white/10 text-white">{trainer.sessions}</td>
                <td className="p-4 border-b border-white/10 text-white">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusClasses(trainer.status, 'Active', 'Pending')}`}>
                    {trainer.status}
                  </span>
                </td>
                <td className="p-4 border-b border-white/10 text-white">
                  {trainer.status === 'Pending' && (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleAcceptTrainer(trainer.id)}
                        disabled={processingTrainer === trainer.id}
                        size="xs"
                        className="bg-[#22c55e] hover:bg-[#16a34a]"
                      >
                        {processingTrainer === trainer.id ? 'Processing...' : 'Approve'}
                      </Button>
                      <Button
                        onClick={() => handleDenyTrainer(trainer.id)}
                        disabled={processingTrainer === trainer.id}
                        size="xs"
                        className="bg-[#ef4444] hover:bg-[#dc2626]"
                      >
                        {processingTrainer === trainer.id ? 'Processing...' : 'Deny'}
                      </Button>
                    </div>
                  )}
                  {trainer.status === 'Active' && (
                    <span className="text-white/50 text-sm">
                      Approved
                    </span>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
    </div>
  );

  const renderSessions = () => (
    <div>
      <div className="flex justify-between items-center mb-5 flex-wrap gap-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#f39c12]">Session Management</h2>
        <div className="flex gap-2.5 items-center">
          <input
            type="text"
            placeholder="Search by trainer name or email..."
            value={trainerSearchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTrainerSearchQuery(e.target.value)}
            className="p-2.5 rounded-lg border-2 border-white/20 bg-white/10 text-white text-base w-full sm:w-[300px] transition-all duration-300"
          />
          <Button
            onClick={loadSessions}
            className="bg-[#3b82f6] hover:bg-[#2563eb]"
          >
            {'\uD83D\uDD0D'} Search
          </Button>
          <Button
            onClick={() => {
              setTrainerSearchQuery('');
              loadSessions();
            }}
            className="bg-[#64748b] hover:bg-[#475569]"
          >
            {'\uD83D\uDD04'} Refresh
          </Button>
        </div>
      </div>
      {trainerSearchQuery && (
        <p className="text-white/70 mb-4">
          Showing all-time sessions for trainer: &quot;{trainerSearchQuery}&quot;
        </p>
      )}
      {!trainerSearchQuery && (
        <p className="text-white/70 mb-4">
          Showing sessions from the last month
        </p>
      )}
      <div className="overflow-x-auto">
      <table className="w-full border-collapse bg-white/5 rounded-lg overflow-hidden">
        <thead>
          <tr>
            <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Client</th>
            <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Trainer</th>
            <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Date</th>
            <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Duration</th>
            <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Status</th>
            <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Revenue</th>
          </tr>
        </thead>
        <tbody>
          {sessions.length === 0 ? (
            <tr>
              <td colSpan={6} className="p-10 border-b border-white/10 text-white text-center">
                No sessions found
              </td>
            </tr>
          ) : (
            sessions.map(session => (
              <tr key={session.id}>
                <td className="p-4 border-b border-white/10 text-white">{session.client}</td>
                <td className="p-4 border-b border-white/10 text-white">{session.trainer}</td>
                <td className="p-4 border-b border-white/10 text-white">{session.date}</td>
                <td className="p-4 border-b border-white/10 text-white">{session.duration} min</td>
                <td className="p-4 border-b border-white/10 text-white">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusClasses(session.status, 'Completed', 'Scheduled')}`}>
                    {session.status}
                  </span>
                </td>
                <td className="p-4 border-b border-white/10 text-white">${session.revenue.toFixed(2)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
    </div>
  );

  const renderVideos = () => (
    <div>
      <h2 className="text-2xl sm:text-3xl font-bold text-[#f39c12] mb-5">Reference Video Management</h2>

      <div className="flex flex-col gap-5 max-w-[500px]">
        <h3 className="text-[#f39c12] mb-5">Add New Video</h3>
        <TextInput
          label="Title"
          value={newVideo.title}
          onChange={(e) => setNewVideo({...newVideo, title: e.target.value})}
          placeholder="Video title"
          classNames={{
            label: 'text-[#f39c12] font-semibold text-base',
            input: 'bg-white/10 border-2 border-white/20 text-white rounded-lg'
          }}
        />
        <Textarea
          label="Description"
          value={newVideo.description}
          onChange={(e) => setNewVideo({...newVideo, description: e.target.value})}
          placeholder="Video description"
          minRows={4}
          classNames={{
            label: 'text-[#f39c12] font-semibold text-base',
            input: 'bg-white/10 border-2 border-white/20 text-white rounded-lg'
          }}
        />
        <div className="flex flex-col gap-2">
          <label className="text-lg font-semibold text-[#f39c12]">Category</label>
          <select
            value={newVideo.category}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setNewVideo({...newVideo, category: e.target.value})}
            className="p-4 rounded-lg border-2 border-white/20 bg-white/10 text-white text-base cursor-pointer"
          >
            <option value="">Select category</option>
            <option value="Obedience">Obedience</option>
            <option value="Walking">Walking</option>
            <option value="Puppy">Puppy</option>
            <option value="Behavior">Behavior</option>
            <option value="Training">Training</option>
          </select>
        </div>
        <TextInput
          label="Video URL"
          value={newVideo.url}
          onChange={(e) => setNewVideo({...newVideo, url: e.target.value})}
          placeholder="https://example.com/video"
          classNames={{
            label: 'text-[#f39c12] font-semibold text-base',
            input: 'bg-white/10 border-2 border-white/20 text-white rounded-lg'
          }}
        />
        <Button
          className="bg-gradient-to-br from-[#e67e22] to-[#f39c12] hover:from-[#d35400] hover:to-[#e67e22] self-start"
          size="lg"
          onClick={handleAddVideo}
        >
          Add Video
        </Button>
      </div>

      <div className="mt-10">
        <h3 className="text-[#f39c12] mb-5">Existing Videos</h3>
        <div className="overflow-x-auto">
        <table className="w-full border-collapse bg-white/5 rounded-lg overflow-hidden">
          <thead>
            <tr>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Title</th>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Category</th>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Upload Date</th>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Status</th>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {referenceVideos.map(video => (
              <tr key={video.id}>
                <td className="p-4 border-b border-white/10 text-white">{video.title}</td>
                <td className="p-4 border-b border-white/10 text-white">{video.category}</td>
                <td className="p-4 border-b border-white/10 text-white">{video.uploadDate}</td>
                <td className="p-4 border-b border-white/10 text-white">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${video.isActive ? 'bg-[rgba(46,204,113,0.2)] text-[#2ecc71]' : 'bg-[rgba(231,76,60,0.2)] text-[#e74c3c]'}`}>
                    {video.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="p-4 border-b border-white/10 text-white">
                  <button
                    className="bg-transparent border border-white/30 text-white px-2.5 py-1 rounded text-xs cursor-pointer transition-all duration-300 hover:bg-white/10"
                    onClick={() => toggleVideoStatus(video.id)}
                  >
                    {video.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );

  const renderBilling = () => (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#f39c12]">Billing & Revenue</h2>
        <Button
          onClick={loadBilling}
          className="bg-[#3b82f6] hover:bg-[#2563eb]"
        >
          {'\uD83D\uDD04'} Refresh
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
        <div className="bg-white/10 rounded-lg p-3 sm:p-5 text-center border border-white/20">
          <div className="text-2xl sm:text-3xl font-bold text-[#f39c12] mb-1">${billingData.sessionIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div className="text-sm text-white/80">Session Income</div>
        </div>
        <div className="bg-white/10 rounded-lg p-3 sm:p-5 text-center border border-white/20">
          <div className="text-2xl sm:text-3xl font-bold text-[#f39c12] mb-1">{billingData.sessionCount.toLocaleString()}</div>
          <div className="text-sm text-white/80">Session Count</div>
        </div>
      </div>
    </div>
  );

  const renderPendingApplications = () => (
    <div>
      <h2 className="text-2xl sm:text-3xl font-bold text-[#f39c12] mb-5">Pending Trainer Applications</h2>
      <div className="max-h-[70vh] overflow-y-auto overflow-x-auto">
        <table className="w-full border-collapse bg-white/5 rounded-lg overflow-hidden">
          <thead className="sticky top-0 bg-[#1e293b] z-10">
            <tr>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Name</th>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Email</th>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Phone</th>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Business Name</th>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Experience (Years)</th>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Rate ($/10min)</th>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Payment Status</th>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Submitted</th>
              <th className="bg-[rgba(243,156,18,0.2)] text-[#f39c12] font-semibold p-4 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pendingApplications.length === 0 ? (
              <tr>
                <td colSpan={9} className="p-10 border-b border-white/10 text-white text-center">
                  No pending applications
                </td>
              </tr>
            ) : (
              pendingApplications.map(app => (
                <tr key={app.id}>
                  <td className="p-4 border-b border-white/10 text-white">{app.first_name} {app.last_name}</td>
                  <td className="p-4 border-b border-white/10 text-white">{app.email}</td>
                  <td className="p-4 border-b border-white/10 text-white">{app.phone_number || 'N/A'}</td>
                  <td className="p-4 border-b border-white/10 text-white">{app.business_name || 'N/A'}</td>
                  <td className="p-4 border-b border-white/10 text-white">{app.experience_years || 'N/A'}</td>
                  <td className="p-4 border-b border-white/10 text-white">${app.first_10_min_rate || 'N/A'}</td>
                  <td className="p-4 border-b border-white/10 text-white">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${app.payment_completed ? 'bg-[rgba(46,204,113,0.2)] text-[#2ecc71]' : 'bg-[rgba(241,196,15,0.2)] text-[#f1c40f]'}`}>
                      {app.payment_completed ? 'Paid' : 'Pending'}
                    </span>
                  </td>
                  <td className="p-4 border-b border-white/10 text-white">
                    {app.application_submitted_at
                      ? new Date(app.application_submitted_at).toLocaleDateString()
                      : 'N/A'}
                  </td>
                  <td className="p-4 border-b border-white/10 text-white">
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleAcceptApplication(app.id)}
                        disabled={processingApplication === app.id}
                        size="xs"
                        className="bg-[#22c55e] hover:bg-[#16a34a]"
                      >
                        {processingApplication === app.id ? 'Processing...' : 'Accept'}
                      </Button>
                      <Button
                        onClick={() => handleDenyApplication(app.id)}
                        disabled={processingApplication === app.id}
                        size="xs"
                        className="bg-[#ef4444] hover:bg-[#dc2626]"
                      >
                        {processingApplication === app.id ? 'Processing...' : 'Deny'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderAccounts = () => (
    <div>
      <h2 className="text-2xl sm:text-3xl font-bold text-[#f39c12] mb-5">Account Management Tools</h2>
      <p className="text-white/85 mb-5 leading-relaxed">
        Remove user accounts directly by username. This action deletes the user and their associated profile data.
        Use with caution--deleted accounts cannot be recovered.
      </p>

      <form className="flex flex-col gap-5 max-w-[500px]" onSubmit={handleDeleteAccount}>
        <TextInput
          label="Username"
          value={deleteUsername}
          onChange={(e) => setDeleteUsername(e.target.value)}
          placeholder="Enter username to delete"
          classNames={{
            label: 'text-[#f39c12] font-semibold text-base',
            input: 'bg-white/10 border-2 border-white/20 text-white rounded-lg'
          }}
        />

        <Button
          type="submit"
          className="bg-gradient-to-br from-[#e74c3c] to-[#c0392b] hover:from-[#c0392b] hover:to-[#a93226] self-start"
          size="lg"
          disabled={deleteLoading}
        >
          {deleteLoading ? 'Deleting...' : 'Delete Account'}
        </Button>
      </form>

      {deleteStatus.message && (
        <div className={deleteStatus.type === 'success'
          ? 'bg-[rgba(46,204,113,0.2)] border border-[#2ecc71] text-[#2ecc71] p-4 rounded-lg mb-5 text-center mt-5'
          : 'bg-[rgba(231,76,60,0.2)] border border-[#e74c3c] text-[#e74c3c] p-4 rounded-lg mb-5 text-center mt-5'
        }>
          {deleteStatus.message}
        </div>
      )}
    </div>
  );

  const renderCurrentTab = (): React.ReactNode => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'clients':
        return renderClients();
      case 'trainers':
        return renderTrainers();
      case 'sessions':
        return renderSessions();
      case 'videos':
        return renderVideos();
      case 'billing':
        return renderBilling();
      case 'pending':
        return renderPendingApplications();
      case 'accounts':
        return renderAccounts();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="font-[Segoe_UI,Tahoma,Geneva,Verdana,sans-serif] bg-gradient-to-br from-[#2c3e50] to-[#34495e] min-h-screen text-white flex flex-col">
      {/* Header */}
      <header className="bg-[#2c3e50] text-white py-5 shadow-[0_2px_10px_rgba(44,62,80,0.2)]">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-5 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-4 cursor-pointer" onClick={onBackToHome}>
            <img
              src="/images/logo.png"
              alt="Pheefee Logo"
              className="w-10 h-10 object-contain rounded-lg"
            />
            <span className="text-xl sm:text-2xl font-bold">Pheefee - Admin</span>
          </div>
          <div className="flex gap-2 sm:gap-4 items-center">
            <Button
              onClick={onBackToHome}
              variant="outline"
              className="border-2 border-[#ecf0f1] text-[#ecf0f1] hover:bg-white/10"
            >
              {'\u2190'} Back to Home
            </Button>
            <Button
              onClick={onLogout}
              className="bg-gradient-to-br from-[#e74c3c] to-[#c0392b] border-2 border-[#e74c3c] hover:from-[#c0392b] hover:to-[#a93226]"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex flex-col md:flex-row max-w-[1400px] mx-auto w-full p-3 sm:p-5">
        {/* Left Sidebar */}
        <div className="w-full md:w-[250px] bg-white/10 rounded-2xl p-3 sm:p-5 mb-4 md:mb-0 md:mr-5 backdrop-blur-[10px] border border-white/20 h-fit overflow-x-auto md:overflow-x-visible flex md:flex-col gap-2 md:gap-0">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-auto md:w-full py-3 md:py-4 px-4 md:px-5 mb-0 md:mb-2.5 rounded-lg text-sm md:text-base font-medium cursor-pointer transition-all duration-300 flex items-center gap-2 md:gap-2.5 whitespace-nowrap
                ${activeTab === tab.id
                  ? 'bg-gradient-to-br from-[#e67e22] to-[#f39c12] border-2 border-[#e67e22] text-white translate-x-[5px]'
                  : 'bg-transparent border-2 border-white/20 text-[#ecf0f1] hover:bg-white/10'
                }`}
            >
              <span className="text-xl">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="flex-1 bg-white/10 rounded-2xl p-4 sm:p-6 md:p-8 backdrop-blur-[10px] border border-white/20 max-h-[80vh] overflow-y-auto">
          {error && (
            <div className="bg-[rgba(231,76,60,0.2)] border border-[#e74c3c] text-[#e74c3c] p-4 rounded-lg mb-5 text-center">
              {error}
            </div>
          )}
          {loading && (
            <div className="text-center text-[#f39c12] text-lg mt-5">
              Loading admin data...
            </div>
          )}
          {renderCurrentTab()}
        </div>
      </div>
    </div>
  );
};

export default AdminPortal;
